import { useEffect, useState, useCallback } from 'react';

const KEY = 'wl-theme';

/** Theme hook with prefers-color-scheme fallback.
 *  Returns { theme, explicit, setTheme, toggle }: `theme` is the effective 'light' | 'dark';
 *  `explicit` is the saved choice, or null when following the system.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(KEY) || null;
    } catch {
      return null;
    }
  });

  // Apply to <html>
  useEffect(() => {
    const html = document.documentElement;
    if (theme) html.setAttribute('data-theme', theme);
    else html.removeAttribute('data-theme');
  }, [theme]);

  // Track system when not explicitly set
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = (e) => setSystemDark(e.matches);
    mql.addEventListener?.('change', handle);
    return () => mql.removeEventListener?.('change', handle);
  }, []);

  const effective = theme ?? (systemDark ? 'dark' : 'light');

  const setTheme = useCallback((t) => {
    setThemeState(t);
    try {
      if (t) localStorage.setItem(KEY, t);
      else localStorage.removeItem(KEY);
    } catch {}
  }, []);

  const toggle = useCallback(() => {
    setTheme(effective === 'light' ? 'dark' : 'light');
  }, [effective, setTheme]);

  return { theme: effective, explicit: theme, setTheme, toggle };
}
